/**
 * TypeScript view of the `spelling_bee` format.
 * Source of truth: `shared/schema/spelling_bee.{definition,submission,solution,progress}.json`.
 * The constants below are the same literals as `backend/apps/puzzletypes/spelling_bee/hive.py`.
 */

export type Localized = Record<string, string | undefined>;
export type Language = "en" | "fr";

export const LANGUAGES: Language[] = ["en", "fr"];
export const LETTER_COUNT = 7;
export const MIN_LENGTH = 4;
export const PANGRAM_BONUS = 7;
export const MAX_WORDS = 500;
export const MAX_WORD_LENGTH = 32;

export interface SpellingBeeDefinition {
  puzzleType: "spelling_bee";
  typeVersion: number;
  language: Language;
  /** The seven letters of the hive, centre included, uppercase ASCII and all different. */
  letters: string;
  /** The letter every word must contain. */
  centre: string;
  rulesText?: Localized;
  meta?: Record<string, unknown>;
}

/** Why a word does not count. The first three are visible rules; the rest come from the server. */
export type WordIssue = "letter_not_allowed" | "too_short" | "missing_centre";

/** Autosave shape. Opaque to the core; this type owns it and its migrations. */
export interface SpellingBeeProgress {
  version: 1;
  /** The words found so far, normalised, in the order they were found. */
  words: string[];
  /** Words the server refused, with the reason code it gave (folded in by `applyVerdict`). */
  rejected?: Record<string, string>;
}

export interface SpellingBeeSubmission {
  words: string[];
}

/** The author's pangram: the proof that the hive has one. Never read by the verdict. */
export interface SpellingBeeSolution {
  pangram: string;
}

/** Picks the viewer's language, falling back to the base language, then English, then any. */
export function localized(value: Localized | null | undefined, locale: string): string {
  if (!value) return "";
  return (
    value[locale] ??
    value[locale.split("-")[0]] ??
    value.en ??
    Object.values(value).find((v) => typeof v === "string" && v.length > 0) ??
    ""
  );
}
